import { useState } from 'react';

import { RegisterImplementationProps } from 'constants/types';
import SignInBase from '../SignInBase';
import LoaderButton from 'components/Button/LoaderButton';
import {
  AuthorizeContextValue,
  withAuthorizeContext,
} from 'pages/Authorize/AuthorizeContext';
import { getProviderFormLabel } from '../utils';
import { Pkce, useOAuthStatePolling } from '../../OAuth/useOAuthStatePolling';

const GUSTO_REDIRECT_URI = `${window.location.protocol}//${window.location.host}/auth/gusto`;

const SignInGusto = ({
  client,
  sandbox,
  onSubmit,
  onMockOAuth,
  setError,
  provider,
  sessionKey,
}: RegisterImplementationProps & AuthorizeContextValue) => {
  const instructions: JSX.Element = (
    <p>
      Gusto authentication will continue in a new window. Please make sure to
      allow popups and finish authenticating through Gusto in the new window.
    </p>
  );

  const [buttonLoading, setButtonLoading] = useState(false);

  // use any for type until Finch common types can be updated
  const gustoClientId = (client as any)?.gustoConfig?.clientId ?? '';
  const gustoRedirectUri =
    (client as any)?.gustoConfig?.redirectUri ?? GUSTO_REDIRECT_URI;

  const onCodeReceived = async (clientCode: string, pkce: Pkce) => {
    await onSubmit({
      clientCode,
      providerRedirectUri: gustoRedirectUri,
    });
  };

  const { loading, openOAuthWindow } = useOAuthStatePolling({
    sessionKey,
    onCodeReceived,
    setError,
  });

  const handleSubmit = async () => {
    setButtonLoading(true);
    const didMockOAuth = await onMockOAuth();

    if (didMockOAuth) {
      setButtonLoading(false);
      return;
    }

    const url = sandbox
      ? new URL(`https://api.gusto-demo.com/oauth/authorize`)
      : new URL(`https://api.gusto.com/oauth/authorize`);
    url.searchParams.set('client_id', gustoClientId);
    url.searchParams.set('redirect_uri', gustoRedirectUri);
    url.searchParams.set('response_type', 'code');
    url.searchParams.set('state', JSON.stringify({ sessionKey }));

    openOAuthWindow(url.toString());
    setButtonLoading(false);
  };

  return (
    <SignInBase
      formLabel={getProviderFormLabel(provider)}
      instructions={instructions}
    >
      <LoaderButton
        isLoading={loading || buttonLoading}
        disabled={loading || buttonLoading}
        onClick={handleSubmit}
        style={{ marginTop: 'auto' }}
        text="Connect"
      />
    </SignInBase>
  );
};

export default withAuthorizeContext(SignInGusto);
